import { useEffect, useState, type FormEvent } from 'react';
import { REGLAGES_PAR_DEFAUT, transporteurDepuisTracking } from '@mosaik/core';
import { ErreurApi } from '../api/client';
import { useIgnorerEnvoi, useMajExpedition } from '../api/hooks';
import type { CommandeListee, DocumentReglages } from '../api/types';
import { formatCourt } from '../format';
import { useToasts } from './Toasts';
import { Repousser } from './Repousser';

/**
 * La fenêtre des cases « Date d'expédition » et « Tracking ».
 *
 * Elle remplace le formulaire Expédition qui vivait dans la fiche : chaque case
 * ouvre la saisie de SA donnée, et le mail au client qui en dépend. Deux
 * réponses possibles, selon où l'on en est :
 *
 * - le mail est parti → on le relit ;
 * - il reste à faire → on saisit (ou corrige) la valeur, et le mail s'ouvre à
 *   relire juste après l'enregistrement, composé sur la commande à jour.
 *
 * Le transporteur ne se choisit que côté tracking : c'est le numéro de suivi
 * qui le désigne le plus souvent — d'où la détection. La date d'expédition ne
 * le demande pas et le laisse tel qu'il est enregistré.
 */
export function SaisieExpedition({
  commande, reglages, champ, onFermer, onVoirMail, onPreparerMail, onAjouterColis,
}: {
  commande: CommandeListee;
  reglages: DocumentReglages | undefined;
  champ: 'expedition' | 'tracking';
  onFermer: () => void;
  /** Ouvre la relecture du mail déjà envoyé. */
  onVoirMail: () => void;
  /** Ouvre le mail à relire, composé sur la commande enregistrée. */
  onPreparerMail: (commande: CommandeListee) => void;
  /** Ouvre l'ajout d'un colis de plus, pour une commande partie en plusieurs envois. */
  onAjouterColis: () => void;
}) {
  const { ajouter } = useToasts();
  const maj = useMajExpedition();
  const ignorer = useIgnorerEnvoi();

  const initiale = champ === 'expedition' ? (commande.dateExpedition ?? '') : (commande.tracking ?? '');
  const [valeur, setValeur] = useState(initiale);
  const [transporteur, setTransporteur] = useState(commande.transporteur ?? '');
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  // Repli tant que les réglages n'ont pas chargé : les défauts du code.
  const transporteurs = Object.keys(reglages?.transit ?? REGLAGES_PAR_DEFAUT.transit);

  const envoi = commande.envois[champ];
  const modifie = valeur !== initiale
    || (champ === 'tracking' && transporteur !== (commande.transporteur ?? ''));
  const titre = champ === 'expedition' ? 'Date d’expédition' : 'Tracking';

  /** Enregistre si besoin ; rend la commande à jour, `null` en cas d'échec. */
  async function enregistrer(): Promise<CommandeListee | null> {
    setErreur(null);
    // La validation fine est côté API : ce contrôle évite seulement un aller-
    // retour évident. Sans transporteur, ni page de suivi ni relevé automatique.
    if (champ === 'tracking' && valeur && !transporteur) {
      setErreur('Choisissez un transporteur : il détermine le suivi et la livraison estimée.');
      return null;
    }
    if (!modifie) return commande;
    try {
      const r = await maj.mutateAsync({
        id: commande.id,
        // Seuls les champs de cette fenêtre : l'API laisse les autres en l'état.
        corps: {
          ...(champ === 'expedition'
            ? { dateExpedition: valeur || null }
            : { tracking: valeur || null, transporteur: transporteur || null }),
        },
      });
      return r;
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'L’enregistrement a échoué.');
      return null;
    }
  }

  /**
   * « Enregistrer sans mail » : la valeur est enregistrée, et le mail au client
   * écarté — l'étape passe à la suite. C'était le rôle d'un second bouton,
   * « Ignorer et passer à la suite », qui faisait la même chose ; cocher la
   * case directement le fait aussi.
   */
  async function enregistrerSeulement(e: FormEvent) {
    e.preventDefault();
    const r = await enregistrer();
    if (!r) return;
    const etat = r.envois[champ].etat;
    if (valeur && etat !== 'envoye' && etat !== 'ignore') {
      try {
        await ignorer.mutateAsync({ id: commande.id, canal: champ });
      } catch (err) {
        setErreur(err instanceof ErreurApi ? err.message : 'Le mail n’a pas pu être écarté.');
        return;
      }
    }
    ajouter(`${titre} enregistré${champ === 'expedition' ? 'e' : ''} pour ${commande.ref}, sans mail au client.`);
    onFermer();
  }

  async function enregistrerEtPreparer() {
    const r = await enregistrer();
    if (r) onPreparerMail(r);
  }

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <form
        className="box" role="dialog" aria-modal="true" aria-label={`${titre} — ${commande.ref}`}
        style={{ width: 520 }} onSubmit={(e) => void enregistrerSeulement(e)}
      >
        <div className="mhead">
          <h2>{titre} — {commande.ref}</h2>
          <button type="button" className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          {/* Le mail d'abord : c'est la question qu'on se pose en ouvrant la
              case — le client est-il au courant ? */}
          <div className="fieldset" style={{ marginBottom: 14 }}>
            <h3 style={{ marginTop: 0 }}>Mail au client</h3>
            <p className="hint" style={{ margin: 0 }}>
              {envoi.etat === 'envoye'
                ? `Envoyé${envoi.deposeLe ? ` le ${formatCourt(envoi.deposeLe.slice(0, 10))}` : ''}`
                  + ` pour ${champ === 'expedition' ? 'cette date' : 'ce numéro'}.`
                : envoi.etat === 'ignore' ? 'Écarté : le client ne sera pas prévenu.'
                : envoi.etat === 'echec' ? `Le dernier envoi a échoué : ${envoi.derniereErreur ?? 'cause inconnue'}.`
                : initiale ? 'Le client n’a pas encore été prévenu.'
                : `Saisissez ${champ === 'expedition' ? 'la date' : 'le numéro'} pour envoyer le mail.`}
            </p>
            {envoi.etat === 'envoye' && (
              <button type="button" className="btn sm" style={{ marginTop: 10 }} onClick={onVoirMail}>
                Voir le mail envoyé
              </button>
            )}
          </div>

          {champ === 'expedition' ? (
            <div className="field">
              <label htmlFor="se-date">Date d&apos;expédition</label>
              <input id="se-date" type="date" value={valeur} onChange={(e) => setValeur(e.target.value)} />
            </div>
          ) : (
            <ChampsTracking
              prefixe="se" valeur={valeur} onValeur={setValeur}
              transporteur={transporteur} onTransporteur={setTransporteur}
              transporteurs={transporteurs}
            />
          )}

          {/* Un second colis a son propre numéro, et son propre mail : il
              s'ajoute à côté du premier, qui reste celui que suit la commande. */}
          {champ === 'tracking' && commande.tracking && !modifie && (
            <div className="fieldset" style={{ marginTop: 14 }}>
              <h3 style={{ marginTop: 0 }}>Autres colis</h3>
              {commande.colis.length > 0 ? (
                <ul style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: 12.5 }}>
                  {commande.colis.map((c) => (
                    <li key={c.tracking}>
                      <span className="mono">{c.tracking}</span>
                      {c.transporteur && ` · ${c.transporteur}`}
                      {' — '}{c.mailEtat === 'envoye' ? 'mail envoyé'
                        : c.mailEtat === 'ignore' ? 'mail écarté'
                        : c.mailEtat === 'echec' ? 'mail non parti'
                        : 'client pas encore prévenu'}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="hint" style={{ margin: '0 0 10px' }}>
                  Plusieurs envois ? Chaque colis a son mail.
                </p>
              )}
              <button type="button" className="btn sm" onClick={onAjouterColis}>
                Ajouter un colis
              </button>
            </div>
          )}

          {erreur && <p className="err" role="alert">{erreur}</p>}
        </div>

        <div className="mfoot">
          <Repousser commande={commande} />
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          {modifie && (
            <button type="submit" className="btn" disabled={maj.isPending || ignorer.isPending}>
              Enregistrer sans mail
            </button>
          )}
          <button
            type="button" className="btn pri" disabled={!valeur || maj.isPending}
            onClick={() => void enregistrerEtPreparer()}
          >
            {maj.isPending
              ? <><span className="spin" /> Enregistrement…</>
              : modifie ? 'Enregistrer et envoyer'
              : envoi.etat === 'envoye' ? 'Refaire le mail' : 'Envoyer'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Numéro de suivi et transporteur, celui-ci déduit du format du numéro.
 *
 * Partagé par la saisie du tracking et l'ajout d'un colis : deux copies de la
 * détection auraient divergé à la première règle ajoutée.
 */
export function ChampsTracking({
  prefixe, valeur, onValeur, transporteur, onTransporteur, transporteurs,
}: {
  /** Préfixe des `id` de champ, unique dans la fenêtre. */
  prefixe: string;
  valeur: string;
  onValeur: (v: string) => void;
  transporteur: string;
  onTransporteur: (t: string) => void;
  transporteurs: string[];
}) {
  // Le format du numéro désigne le transporteur : DHL préfixe en FRT-, Dachser
  // émet des SSCC de 20 chiffres, DB Schenker des numéros de 18 ou une
  // référence en FR + code d'agence, FedEx des numéros de 12, DPD de 14.
  const detection = transporteurDepuisTracking(valeur);
  const detectionDivergente = Boolean(detection) && Boolean(transporteur)
    && detection!.transporteur !== transporteur;

  // Pré-sélection quand rien n'est choisi. On ne remplace jamais un choix déjà
  // fait sans le dire : la divergence est signalée sous le champ.
  useEffect(() => {
    if (detection && !transporteur) onTransporteur(detection.transporteur);
  }, [detection?.transporteur, transporteur]);

  return (
    <>
      <div className="field">
        <label htmlFor={`${prefixe}-tk`}>Numéro de tracking</label>
        <input
          id={`${prefixe}-tk`} className="mono" value={valeur} placeholder="00340326850353…"
          onChange={(e) => onValeur(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor={`${prefixe}-tr`}>Transporteur</label>
        <select id={`${prefixe}-tr`} value={transporteur} onChange={(e) => onTransporteur(e.target.value)}>
          <option value="">—</option>
          {transporteurs.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>

        {detection && !detectionDivergente && (
          <p className="indice" role="status">
            {detection.confiance === 'certaine'
              ? `Reconnu d'après le ${detection.motif}.`
              : `Probablement ${detection.transporteur} (${detection.motif}) — à confirmer.`}
          </p>
        )}
        {detectionDivergente && (
          <p className="indice avert">
            Ce numéro ressemble à du <strong>{detection!.transporteur}</strong> ({detection!.motif}).{' '}
            <button
              type="button" className="btn sm"
              onClick={() => onTransporteur(detection!.transporteur)}
            >
              Choisir {detection!.transporteur}
            </button>
          </p>
        )}
        {valeur && !detection && (
          <p className="indice">
            Format non reconnu : choisissez le transporteur, il détermine la date de livraison
            estimée.
          </p>
        )}
      </div>
    </>
  );
}
